import { useWorkspaceText } from '../interface-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useNamespaceCopy } from '@/i18n/ns';
import runtimeCopy from '@/i18n/ns/workspace-knowledge-runtime';
import type { PropsWithChildren } from 'react';
import { useAuiState } from '@assistant-ui/react';
import { StatusPill } from '@/components/ui/kit';
import type { AgentRunCard, ServerThreadMeta } from '@/server-threads';
import type { AgentRunStatus } from './api';
import { useOptionalWorkspace } from '../context';
import { WorkspaceIcon } from '../icons';
import { runStatusLabel, runStatusTone } from './run-status';
import { creditsText, durationLong, runDate } from './time';
import './agents.css';

// A run chat opens with its run card (agent redesign 2026-10-09): who or what
// started the run, its version and pinned Skills, how long it took and, for
// the person it ran for or whoever manages billing, the credits it used. The card
// stands in for the run's internal task message, and the run's "continue"
// messages after approvals are not shown at all; the model's history keeps
// both. Everything comes from the run record the server attached to the chat
// (server agents/run-card.mjs), never from the messages themselves.

type RunRecord = NonNullable<ServerThreadMeta['agentRun']>;

function cardTitle(
  card: AgentRunCard,
  status: string,
  viewerId: string | undefined,
  copy: Readonly<Record<keyof typeof runtimeCopy.en, string>> = runtimeCopy.en,
) {
  const live = status === 'queued' || status === 'running';
  if (card.triggerType === 'test')
    return `${copy.testRunBy.replace('{name}', copy.you)}: ${card.agentName}`;
  if (card.triggerType === 'schedule')
    return live
      ? `${card.agentName} · ${copy.runRunning}`
      : `${card.agentName} · ${copy.runScheduled}`;
  const by = card.runBy?.userId === viewerId ? copy.you : (card.runBy?.name ?? copy.formerMember);
  return `${by} · ${card.agentName}`;
}

// "Version 2, following Competitor price check v4"
function versionPart(
  card: AgentRunCard,
  locale: string,
  copy: Readonly<Record<keyof typeof runtimeCopy.en, string>> = runtimeCopy.en,
) {
  const version =
    card.triggerType === 'test'
      ? copy.draftRevision.replace('{version}', String(card.agentRevision))
      : copy.versionLabel.replace('{version}', String(card.agentRevision));
  const skills = card.skills.map((skill) => `${skill.name} v${skill.revision}`);
  if (!skills.length) return version;
  const listed = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(skills);
  return `${version}, ${copy.following.replace('{skills}', listed)}`;
}

// "Took 3 min 4 s and used 3.1 credits", or where the run is now.
function outcomePart(
  card: AgentRunCard,
  status: string,
  locale = 'en-GB',
  copy: Readonly<Record<keyof typeof runtimeCopy.en, string>> = runtimeCopy.en,
) {
  if (status === 'queued') return copy.runQueued;
  if (status === 'running') return copy.runRunning;
  if (status === 'awaiting_approval') return copy.runApproval;
  if (card.durationMs === null) return '';
  const used =
    card.credits === undefined
      ? ''
      : ` ${copy.andUsed.replace('{credits}', creditsText(card.credits, locale, copy))}`;
  return copy.took
    .replace('{duration}', durationLong(card.durationMs, locale))
    .replace('{used}', used);
}

/** "Couldn't read: Google Ads and Google Analytics." for a completed run that could not read some
 * connections, else null. A change that does not go through fails the run, so every failed
 * connection call in a completed run was a read. The server names them (projection.mjs). */
export function unavailableSourcesLine(
  sources: readonly string[] | undefined,
  status: string,
  locale = 'en-GB',
  copy: Readonly<Record<keyof typeof runtimeCopy.en, string>> = runtimeCopy.en,
) {
  if (!sources?.length || status !== 'completed') return null;
  const listed = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(
    sources,
  );
  return copy.runCouldNotRead.replace('{sources}', listed);
}

export function runCardDetail(
  card: AgentRunCard,
  status: string,
  locale = 'en-GB',
  copy: Readonly<Record<keyof typeof runtimeCopy.en, string>> = runtimeCopy.en,
) {
  const at = card.scheduledFor ?? card.startedAt ?? card.createdAt;
  return [
    at ? runDate(at, locale) : '',
    versionPart(card, locale, copy),
    outcomePart(card, status, locale, copy),
  ]
    .filter(Boolean)
    .map((part) => `${part}.`)
    .join(' ');
}

export function RunCard({ run }: { run: RunRecord & { card: AgentRunCard } }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const locale = useChatInterfaceLocale();
  const copy = useNamespaceCopy(runtimeCopy);
  const ws = useOptionalWorkspace();
  const status = run.status as AgentRunStatus;
  const title = cardTitle(run.card, run.status, ws?.user?.id, copy);
  const unreadable = unavailableSourcesLine(run.card.unavailableSources, run.status, locale, copy);
  // The person who ran it (the server names the agent to them alone) opens it on its Runs.
  const agentId = run.agentId;
  return (
    <section className="ws-run-card" aria-label={uiText('Run')} data-status={run.status}>
      <div className="ws-run-card-head">
        <WorkspaceIcon name="agents" size={16} />
        {agentId && ws ? (
          <button
            type="button"
            className="ws-run-card-open"
            title={uiText('Open the agent’s runs')}
            onClick={() => ws.openCanvas({ kind: 'agent', id: agentId, tab: 'runs' })}
          >
            {title}
          </button>
        ) : (
          <strong>{title}</strong>
        )}{' '}
        <StatusPill tone={runStatusTone(status)}>
          {runStatusLabel(status, copy) ?? run.status}
        </StatusPill>
      </div>{' '}
      <p>{runCardDetail(run.card, run.status, locale, copy)}</p>
      {unreadable && <p>{unreadable}</p>}
    </section>
  );
}

/** What a user message of a run chat shows: the run card in place of the run's task message,
 * nothing for a resume's "continue" message, and the message itself otherwise (the person's own
 * follow-ups, or a run chat from a server that sent no card). */
export function runMessageView(
  run: ServerThreadMeta['agentRun'] | undefined,
  messageId: string,
): 'card' | 'hidden' | 'message' {
  const ids = run?.internalMessageIds ?? [];
  if (!run || !ids.includes(messageId)) return 'message';
  if (messageId !== ids[0]) return 'hidden';
  return run.card ? 'card' : 'message';
}

/** The thread's user-message wrapper (thread.tsx UserMessageWrapper). */
export function RunTaskMessage({ children }: PropsWithChildren) {
  const ws = useOptionalWorkspace();
  const messageId = useAuiState((state) => state.message.id);
  const run = ws?.threadMeta?.agentRun;
  const view = runMessageView(run, messageId);
  if (view === 'hidden') return null;
  if (view === 'card' && run?.card) return <RunCard run={{ ...run, card: run.card }} />;
  return <>{children}</>;
}
